import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import toast from "react-hot-toast";

import GenericTable from "@/components/ui/GenericTable";

import {
    getTrailOffers,
    updateTrailOffer,
} from "@/services/offers";

import { useScreenPermission } from "@/hooks/useScreenPermission";

import Pagination from "../common/Pagination";
import TrailOfferModal from "./AddTrailOffer";

const ITEMS_PER_PAGE = 20;

export default function TrailOfferList() {
    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [editingOffer, setEditingOffer] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const { canCreate, canUpdate } =
        useScreenPermission("trails offer");

    const fetchOffers = async (
        page: number,
        searchValue: string
    ) => {
        try {
            setLoading(true);

            const response = await getTrailOffers(
                undefined,
                page,
                searchValue
            );

            const responseData = response.data as any;

            if (response.status === 200) {
                const results = Array.isArray(responseData)
                    ? responseData
                    : responseData?.results || [];

                setOffers(results);

                setTotalPages(
                    Array.isArray(responseData)
                        ? 1
                        : responseData?.total_pages || 1
                );

                setTotalCount(
                    Array.isArray(responseData)
                        ? responseData.length
                        : responseData?.count || 0
                );
            }
        } catch (error) {
            toast.error("Failed to fetch offers");
        } finally {
            setLoading(false);
        }
    };

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setCurrentPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    // Fetch data when page or search changes
    useEffect(() => {
        fetchOffers(currentPage, debouncedSearch);
    }, [currentPage, debouncedSearch]);

    const handleToggleStatus = async (item: any) => {
        try {
            const formData = new FormData();

            formData.append(
                "Status",
                String(!item.Status)
            );

            const response = await updateTrailOffer(
                item.id,
                formData
            );

            if (response.status === 200) {
                toast.success(
                    "Status updated successfully"
                );

                setOffers((prev) =>
                    prev.map((x) =>
                        x.id === item.id
                            ? {
                                ...x,
                                Status: !x.Status,
                            }
                            : x
                    )
                );
            }
        } catch (error) {
            toast.error(
                "Failed to update status"
            );
        }
    };

    const handleAdd = () => {
        setEditingOffer(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item: any) => {
        setEditingOffer(item);
        setIsModalOpen(true);
    };

    const columns = [
        {
            key: "Image",
            label: "Image",
            render: (value: string) => {
                const imageBaseUrl =
                    import.meta.env.VITE_BASE_URL.replace(
                        "/api",
                        ""
                    );

                const imageUrl = value
                    ? `${imageBaseUrl}${value}`
                    : null;

                return imageUrl ? (
                    <img
                        src={imageUrl}
                        alt="Offer"
                        className="w-16 h-16 rounded-xl object-cover border"
                        onError={(e) => {
                            e.currentTarget.src =
                                "https://via.placeholder.com/100?text=No+Image";
                        }}
                    />
                ) : (
                    <div className="w-16 h-16 rounded-xl border flex items-center justify-center text-xs text-gray-500">
                        No Image
                    </div>
                );
            },
        },

        {
            key: "Days",
            label: "Days",
            render: (value: string) => (
                <p className="font-semibold text-gray-800">
                    {value}
                </p>
            ),
        },

        {
            key: "Status",
            label: "Status",
            render: (_: any, row: any) => (
                <button
                    onClick={() => {
                        if (canUpdate) {
                            handleToggleStatus(row);
                        }
                    }}
                    disabled={!canUpdate}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        row.Status
                            ? "bg-green-500"
                            : "bg-red-500"
                    } ${
                        !canUpdate
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                    }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            row.Status
                                ? "translate-x-6"
                                : "translate-x-1"
                        }`}
                    />
                </button>
            ),
        },
    ];

    return (
        <>
            <div className="space-y-6">

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        placeholder="Search trail offers..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                </div>

                <GenericTable
                    title="Trail Offer"
                    buttonName={
                        canCreate
                            ? "Add Trail Offer"
                            : ""
                    }
                    handleAdd={
                        canCreate
                            ? handleAdd
                            : undefined
                    }
                    columns={columns}
                    data={offers}
                    loading={loading}
                    onEdit={
                        canUpdate
                            ? handleEdit
                            : undefined
                    }
                    emptyMessage={
                        search.trim()
                            ? "No trail offers found"
                            : "No trail offers found"
                    }
                />

                {!loading && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={(page) =>
                            setCurrentPage(page)
                        }
                    />
                )}
            </div>

            {isModalOpen && (
                <TrailOfferModal
                    key={
                        editingOffer?.id || "new"
                    }
                    offer={editingOffer}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingOffer(null);
                    }}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        setEditingOffer(null);

                        fetchOffers(
                            currentPage,
                            debouncedSearch
                        );
                    }}
                />
            )}
        </>
    );
}
